'use client';

import React from 'react';
import PageHeader from '../../components/shared/PageHeader';
import PageFooter from '../../components/shared/PageFooter';
import { CALCULATORS, GUIDES } from './calculators';

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif';
const OMBRE = 'linear-gradient(135deg, #6ee7b7, #06dffa, #dcccfe)';

const ombre: React.CSSProperties = {
  background: OMBRE,
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
};

const LAVENDER = 'linear-gradient(90deg, #E8DEFF 0%, #E8DEFF 40%, #FFFFFF 50%, #E8DEFF 60%, #E8DEFF 100%)';

// The gradient is 200% wide so the white band has somewhere to travel; without
// backgroundSize the animation has nothing to move and the text sits static.
const sparkleLavender: React.CSSProperties = {
  background: LAVENDER,
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
  animation: 'calcSparkle 3s ease-in-out infinite',
  fontWeight: 600,
  textDecoration: 'underline',
  textDecorationColor: 'rgba(232,222,255,0.55)',
  textUnderlineOffset: '3px',
};

const link: React.CSSProperties = {
  ...ombre,
  textDecoration: 'underline',
  // The underline inherits `color`, which background-clip has made
  // transparent, so it has to be set explicitly or it doesn't draw.
  textDecorationColor: '#06dffa',
  fontWeight: 600,
};

// Same stack the help centre and the comparison page use for their headings.
const h2: React.CSSProperties = {
  fontSize: '20px',
  fontWeight: 700,
  lineHeight: 1.35,
  color: 'white',
  margin: '38px 0 14px',
  fontFamily: 'var(--font-bricolage), -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif',
};

const p: React.CSSProperties = {
  fontSize: '16px',
  lineHeight: 1.8,
  color: 'rgba(255,255,255,0.8)',
  marginBottom: '18px',
};

const CalculatorsContent = () => (
  <div
    style={{
      minHeight: '100vh',
      background:
        'linear-gradient(180deg, #0a0f32 0%, #0a1230 8%, #0c1a35 18%, #0e2240 30%, #102a48 45%, #0e2240 60%, #0c1a35 75%, #0a1230 88%, #0a0f32 100%)',
      color: 'white',
      fontFamily: FONT,
    }}
  >
    <style>{`
      @keyframes calcSparkle {
        0%, 100% { background-position: 200% center; }
        50% { background-position: -200% center; }
      }
      @media (prefers-reduced-motion: reduce) {
        .calc-guide-link { animation: none !important; }
      }
    `}</style>
    <PageHeader />
    {/* 52px here plus the h1's own 24px puts the heading 76px under the
        header, matching /splitwise-alternative. The help centre sits at 24px,
        but its heading is an eyebrow above a sidebar layout rather than the
        page's opening line. */}
    <div style={{ maxWidth: '680px', margin: '0 auto', padding: '52px 24px 48px' }}>
      {/* Matches the help centre's hero (app/support/SupportContent.tsx):
          same 23px/700, same 24px off the top, same #E8DEFF lavender and SF
          Pro Display stack. Left-aligned rather than centred — the support
          hero centres only because it sits above a two-column layout, and
          this page is a single left-aligned column. */}
      {/* Same treatment as the /text hero: 32px at weight 600 in Bricolage,
          over the lavender-to-white gradient. */}
      <h1
        style={{
          fontSize: '32px',
          fontWeight: 600,
          lineHeight: 1.3,
          letterSpacing: '-0.02em',
          fontFamily:
            'var(--font-bricolage), -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
          background: LAVENDER,
          backgroundSize: '200% auto',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          color: 'transparent',
          marginTop: '24px',
          marginBottom: '20px',
          textAlign: 'center',
        }}
      >
        Bill Splitting Calculators
      </h1>

      <p style={{ ...p, fontSize: '17px' }}>
        Free tools for working out who owes what. For when an even split isn't fair, when the bill
        has fifteen items on it, or when a whole trip's worth of costs needs untangling. No
        signup required for any of them.
      </p>

      <div style={{ marginTop: '30px' }}>
        {CALCULATORS.map((c) => (
          <a
            key={c.href}
            href={c.href}
            style={{
              display: 'block',
              textDecoration: 'none',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '14px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '8px',
              }}
            >
              {/* No "Calculator" badge any more — everything in this list is
                  one, so it labelled nothing. */}
              <span style={{ fontSize: '18px', fontWeight: 700, color: 'white' }}>{c.title}</span>
            </div>
            <p
              style={{
                fontSize: '15px',
                lineHeight: 1.7,
                color: 'rgba(255,255,255,0.7)',
                margin: '0 0 12px',
              }}
            >
              {c.blurb}
            </p>
            <span style={{ fontSize: '14px', fontWeight: 600 }}>
              <span
                style={{
                  ...ombre,
                  textDecoration: 'underline',
                  // Inherits `color`, which background-clip set transparent, so
                  // it has to be named or the underline never paints.
                  textDecorationColor: 'rgba(6,223,250,0.55)',
                  textUnderlineOffset: '3px',
                }}
              >
                {c.cta}
              </span>
              <span style={{ color: '#7fe9ff', whiteSpace: 'nowrap' }}> →</span>
            </span>
          </a>
        ))}
      </div>

      <h2 style={h2}>
        When a calculator helps
      </h2>
      <p style={p}>
        If everyone is paying for the same thing, split it evenly and stop. The arithmetic is
        rarely worth the conversation. Reach for a calculator when people got materially
        different things: one room has an ensuite and another has bunk beds, or half the table
        drank and half didn't.
      </p>
      {/* /receipts and /trips are linked from prose rather than listed as
          cards above. They're the app, not calculators, and saying so is more
          use to a reader than filing them under a heading they don't fit. */}
      <p style={p}>
        Some splits don't need one. A restaurant bill already lists what everyone had, so{' '}
        <a href="/receipts" style={link}>
          splitting a bill automatically by item from scanning a photo on Tabster
        </a>{' '}
        beats typing it into a form. And a whole trip's worth of costs isn't one sum to divide. That
        that warrants{' '}
        <a href="/trips" style={link}>
          a trip on Tabster
        </a>{' '}
        that collects scanned receipts and other expenses as they happen, so you can easily divvy
        up at the end of the trip.
      </p>

      <h2 style={h2}>
        Guides
      </h2>
      <ul style={{ ...p, paddingLeft: '22px', listStyle: 'disc' }}>
        {GUIDES.map((g) => (
          <li key={g.href} style={{ marginBottom: '8px' }}>
            <a
              href={g.href}
              style={{ textDecoration: 'none', display: 'inline' }}
            >
              <span className="calc-guide-link" style={sparkleLavender}>
                {g.label}
              </span>
              <span style={{ color: '#E8DEFF', whiteSpace: 'nowrap' }}> →</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
    <PageFooter subtitle="Free download. No manual inputs. Just fun 🤠" />
  </div>
);

export default CalculatorsContent;
